import { Component } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { tap, first } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { DictionaryItem, DictionaryItemDefinition, DictionarySimplePageComponent } from 'src/app/shared/components/dictionary-simple-page/dictionary-simple-page.component';
import { EsraPipingAPIClient, ServiceViewModel } from 'src/app/shared/models/autogenerated-piping';
import { ServiceNoteUsageComponent } from '../dialogs/service-note-usage/service-note-usage.component';
import { ServiceAddEditComponent } from '../dialogs/service-add-edit/service-add-edit.component';
import { DictionaryDeleteComponent } from '../dialogs/dictionary-delete/dictionary-delete.component';
import { InsightsService } from 'src/app/shared/services/insights.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { DictionaryExportService } from '../services/dictionary-export.service';

@Component({
    selector: 'app-services',
    templateUrl: '../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.html',
    styleUrls: ['../../../../shared/components/dictionary-simple-page/dictionary-simple-page.component.scss'],
    standalone: false
})
export class ServicesComponent extends DictionarySimplePageComponent<ServiceViewModel> {
  constructor(
    private esraApiClient: EsraPipingAPIClient,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private insightsService: InsightsService,
    private dictionaryExportService: DictionaryExportService
  ) {
    super();
    this.exportFn = () => this.dictionaryExportService.exportServices();
  }

  protected childOnInitBefore(): void { /* do nothing */ }
  protected childOnInitAfter(): void { /* do nothing */ }

  protected searchAll() {
    this.esraApiClient.getAllCurrentServices()
      .pipe(
        indicate(this.searching$),
        first(),
        tap(services => {
          this.totalCount = services.length;
          this.dataSource.data = services;
        })
      ).subscribe();
  }

  protected initializeDictionaryDefinition(): DictionaryItemDefinition<ServiceViewModel>{
    return {
      displayStatusColumn: true,
      dictionaryItems: [ new DictionaryItem('Last Updated', 'updated', 'standard', (item: ServiceViewModel) =>  this.getDate(item) ),
        new DictionaryItem('Service',  'name', 'extended')
      ]
    };
  }

  protected getDate(item: ServiceViewModel): string {
    return String(item.updated.getMonth() + 1).padStart(2, '0') + '/' + String(item.updated.getDate()).padStart(2, '0') + '/' + item.updated.getFullYear();
  }

  protected getFilterPredicate(record: ServiceViewModel, parametersMap: Map<string, string>): boolean {
    const searchQuery = parametersMap.get(DictionarySimplePageComponent.SEARCH_QUERY_NAME)?.toLowerCase() || '';
    const isActiveFilter = parametersMap.get(DictionarySimplePageComponent.IS_ACTIVE_NAME);

    const matchesSearchQuery = record.updated.toString().toLowerCase().includes(searchQuery) || record.name.toLowerCase().includes(searchQuery);

    if (isActiveFilter == null) {
        return matchesSearchQuery;
    } else {
        return matchesSearchQuery && record.isActive === JSON.parse(isActiveFilter);
    }
  }

  protected getNoMatchMessage(): string {
    return "No services match provided search criteria";
  }

  protected createNew(): void {
    this.openEditor(null);
  }

  protected editItem(service: ServiceViewModel): void {
    this.openEditor(service);
  }

  protected editItemReferenced(service) {
    this.snackBarService.showSnackBar(
      true,
      `Service, ${service.name}, cannot be edited because it is in use`
    );
  }

  private openEditor(service: ServiceViewModel | null): void {
    const dialogRef: MatDialogRef<ServiceAddEditComponent, boolean> = this.dialog.open<
      ServiceAddEditComponent,
      ServiceViewModel | null,
      boolean
    >(ServiceAddEditComponent, {
      width: '500px',
      maxWidth: '90vw',
      disableClose: true,
      data: service
    });

    dialogRef.afterClosed().pipe(first()).subscribe((saved: boolean | undefined) => {
      if (saved) {
        this.searchAll();
      }
    });
  }

  protected deleteItem(item) {
    this.dialog.open(DictionaryDeleteComponent, {
      width: '40%',
      height: '23%',
      data: {
        item: item,
        objectName: item.name,
        objectType: 'service'
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.esraApiClient.archiveService(item.id).subscribe({
          next: () => {
            this.snackBarService.showSnackBar(
              true,
              `Service "${item.name}" was successfully archived.`,
              'mat-snack-bar-info'
            );
            this.searchAll();
          },
          error: (error) => {
            this.snackBarService.showSnackBar(
              true,
              `Sorry, something went wrong. Error trying to archive service "${item.name}".`,
              'mat-snack-bar-error'
            );
            this.insightsService.logException(error);
          },
        });
      }
    });
  }

  protected getUsageInformation(item): void {
    (this.dialog.open(ServiceNoteUsageComponent, {
      width: '40%',
      height: '40%',
      data: item
    })).afterClosed().subscribe(() => {
      this.searchAll();
    });
  }

  protected updateStatus(item: ServiceViewModel) {
    this.esraApiClient.toggleIsActiveService(item.isActive, [item.id]).pipe(indicate(this.searching$), first(),
        tap(() => this.search())).subscribe({error: () => this.searchAll()});
  }
}
